import Datatable from "./datatable_controller";

// Connects to data-controller="audit-trail-datatable"
export default class extends Datatable {
  static targets = ["filter", "userFilter", "tab", "searchInput"]

  static values = {
    todayLabel: { type: String, default: "Today" },
    yesterdayLabel: { type: String, default: "Yesterday" },
  }

  connect() {
    this.activeCategory = "all"
    this.searchQuery = ""
    super.connect()
  }

  // Single full-width "activity" column; each row is a rendered card. Date-group
  // headers are inserted client-side after each draw (insertDateGroups).
  get columns() {
    return [
      { data: "activity", orderable: false }
    ]
  }

  get searching() {
    return false
  }

  get ordering() {
    return true
  }

  get order() {
    return []
  }

  // Category tab bar: highlight the clicked tab and reload with its filter.
  selectCategory(event) {
    event.preventDefault()
    const clicked = event.currentTarget
    this.activeCategory = clicked.dataset.category || "all"
    this.tabTargets.forEach((tab) => tab.classList.toggle("active", tab === clicked))
    if (this.dataTable) this.dataTable.ajax.reload()
  }

  // Search box: debounce, then reload with the query (server filters on derived
  // fields). Overrides the base runSearch, which uses DataTables' built-in
  // search param — we send our own `search_query` via manipulateData instead.
  runSearch(event) {
    this.searchQuery = event.target.value || ""
    clearTimeout(this._searchTimer)
    this._searchTimer = setTimeout(() => {
      if (this.dataTable) this.dataTable.ajax.reload()
    }, 250)
  }

  applyActionFilter(event) {
    event.preventDefault()
    
    // Update datatable with action filters
    this.dataTable.ajax.reload()
  }

  removeActionFilter(event) {
    event.preventDefault()
    // Uncheck all filter checkboxes
    this.filterTargets.forEach(checkbox => checkbox.checked = false)
    
    // Reload datatable without filters
    this.dataTable.ajax.reload()
  }

  applyUserFilter(event) {
    event.preventDefault()
    
    // Update datatable with user filters
    this.dataTable.ajax.reload()
  }

  removeUserFilter(event) {
    event.preventDefault()
    // Uncheck all user filter checkboxes
    this.userFilterTargets.forEach(checkbox => checkbox.checked = false)
    
    // Reload datatable without user filters
    this.dataTable.ajax.reload()
  }

  manipulateData(data) {
    // Call parent manipulateData first
    data = super.manipulateData(data)
    
    // Add action filters with validation
    const checkedFilters = this.filterTargets.filter(checkbox => checkbox.checked)
    if (checkedFilters.length > 0) {
      const actionFilters = checkedFilters
        .map(checkbox => this.sanitizeActionFilter(checkbox.value))
        .filter(value => value !== null) // Remove invalid values
      
      if (actionFilters.length > 0) {
        data.action_filters = actionFilters.join(',')
      }
    }
    
    // Add user filters with validation
    const checkedUserFilters = this.userFilterTargets.filter(checkbox => checkbox.checked)
    if (checkedUserFilters.length > 0) {
      const userFilters = checkedUserFilters
        .map(checkbox => this.sanitizeUserFilter(checkbox.value))
        .filter(value => value !== null) // Remove invalid values
      
      if (userFilters.length > 0) {
        data.user_filters = userFilters.join(',')
      }
    }

    // Category tab (omit 'all' — it's a no-op server-side)
    if (this.activeCategory && this.activeCategory !== "all") {
      data.category_filters = this.activeCategory
    }

    // Derived-field search
    if (this.searchQuery && this.searchQuery.trim() !== "") {
      data.search_query = this.searchQuery.trim()
    }

    return data
  }

  // Merge our date-group insertion into the base draw handling. The base
  // drawCallback manages empty-state + pagination; we run grouping after it.
  get dataTableOptions() {
    const options = super.dataTableOptions
    const baseDraw = options.drawCallback
    const self = this
    options.drawCallback = function (settings) {
      if (baseDraw) baseDraw.call(this, settings)
      self.insertDateGroups(this.api())
    }
    return options
  }

  // Insert "Today / Yesterday / Mon DD, YYYY" header rows before the first card
  // of each date group. Rows arrive newest-first (server orders desc), so a
  // single pass over the page produces correctly ordered group headers.
  insertDateGroups(api) {
    const tbody = api.table().body()
    if (!tbody) return

    // Clear any group headers from the previous draw.
    tbody.querySelectorAll("tr.audit-group-row").forEach((tr) => tr.remove())

    const groupKey = (iso) => {
      const d = new Date(iso)
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
    }
    const groupLabel = (iso) => {
      const d = new Date(iso)
      const now = new Date()
      const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate())
      const days = Math.round((startOf(now) - startOf(d)) / 86400000)
      if (days === 0) return this.todayLabelValue
      if (days === 1) return this.yesterdayLabelValue
      return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })
    }

    let lastKey = null
    api.rows({ page: "current" }).every(function () {
      const iso = this.data().timestamp_iso
      if (!iso) return
      const node = this.node()
      const key = groupKey(iso)
      if (key !== lastKey) {
        lastKey = key
        const header = document.createElement("tr")
        header.className = "audit-group-row"
        const cell = document.createElement("td")
        cell.className = "audit-group-label"
        cell.textContent = groupLabel(iso)
        header.appendChild(cell)
        node.parentNode.insertBefore(header, node)
      }
    })
  }

  // Validate and sanitize action filter values
  sanitizeActionFilter(value) {
    const validActions = ['create', 'update', 'destroy', 'bulk_ticket_toggle']
    
    if (!value || typeof value !== 'string') {
      return null
    }
    
    // Remove any HTML/script tags and trim whitespace
    const cleaned = value.replace(/<[^>]*>/g, '').trim()
    
    // Only allow valid action types
    return validActions.includes(cleaned) ? cleaned : null
  }

  // Validate and sanitize user filter values
  sanitizeUserFilter(value) {
    if (!value || typeof value !== 'string') {
      return null
    }
    
    // Remove any HTML/script tags and trim whitespace
    const cleaned = value.replace(/<[^>]*>/g, '').trim()
    
    // Allow 'system' or numeric user IDs only
    if (cleaned === 'system') {
      return 'system'
    }
    
    // Validate numeric user IDs (positive integers only)
    if (/^\d+$/.test(cleaned)) {
      const userId = parseInt(cleaned, 10)
      return userId > 0 ? userId.toString() : null
    }
    
    return null
  }
}